import { action, computed, observable, runInAction } from 'mobx';
import ErrorUtil from '../../error/ErrorUtil';
import API from '../../util/ApiUtil';
import LanguageStore from '../LanguageStore';
import { RootStore } from '../RootStore';
import RouteStore from '../RouteStore';
import { ContentType, Image } from './MovieRentalCatalogStore';

const THIRTY_SECONDS = 30 * 1000;

export interface MovieDetails {
  id: number;
  title: string;
  originalTitle: string;
  description: string;
  year: number;
  price: string;
  duration: number;
  averageEpisodeDuration: number;
  subtitles: string[];
  audioTracks: string[];
  images: Image;
  resolutions: string[];
  countries: string[];
  packetIds: number[];
}

export interface MovieDetailsHolder {
  movieDetails?: MovieDetails;
  status: string;
}

class MovieDetailsStore {
  @observable accessor modalOpen: boolean = false;
  @observable accessor selectedItemId: number;
  @observable accessor selectedItemType: ContentType;
  @observable accessor movieDetailsMap: Map<string, MovieDetailsHolder> = new Map();

  private languageStore: LanguageStore;
  private routeStore: RouteStore;

  constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    this.routeStore = rootStore.routeStore;
  }

  public async loadMovieDetails(movieId: number, type: string) {
    if (!movieId || !type || this.fetchMovieDetailsNotRequired(movieId, type)) {
      return Promise.resolve();
    }
    runInAction(() => {
      this.movieDetailsMap.set(this.movieDetailsKey(movieId, type), {
        status: 'FETCHING',
      });
    });
    return this.fetchMovieDetails(movieId, type);
  }

  public getMovieDetails(movieId: number, type: string): MovieDetails {
    return this.getMovieDetailsHolder(movieId, type).movieDetails;
  }

  public isFetchingMovieDetails(movieId: number, type: string) {
    return ['UNINITIALIZED', 'FETCHING'].includes(this.getMovieDetailsHolder(movieId, type).status);
  }

  @computed
  public get modalCover() {
    const movie = this.getMovieDetails(this.selectedItemId, this.selectedItemType);
    return movie
      ? {
          image: {
            srcset: movie.images.poster,
            width: 280,
            alt: movie.title,
          },
          backgroundColor: '#000000',
        }
      : null;
  }

  @action
  public closeModal() {
    this.selectedItemId = null;
    this.selectedItemType = null;
    this.modalOpen = false;
  }

  @action
  public openMovieModalByParams() {
    const { assets, series } = this.routeStore.decodedParams;
    if ((assets || series) && !this.modalOpen) {
      this.modalOpen = true;
      this.selectedItemType = assets ? ContentType.ASSETS : ContentType.SERIES;
      this.selectedItemId = assets || series;
      return true;
    }
    return false;
  }

  private async fetchMovieDetails(movieId: number, type: string) {
    const lang = this.languageStore.currentLanguage;
    try {
      const response = await API.get(`/movie-rental/${lang}/vod/${type}/${movieId}`, {
        timeout: THIRTY_SECONDS,
      });
      this.handleMovieRentalResult(movieId, type, response.data);
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      this.movieDetailsMap.set(this.movieDetailsKey(movieId, type), {
        status: 'NOT_FOUND',
      });
    }
  }

  private handleMovieRentalResult = (movieId: number, type: string, result: MovieDetails) => {
    runInAction(() => {
      this.movieDetailsMap.set(this.movieDetailsKey(movieId, type), {
        status: 'FETCHED',
        movieDetails: result,
      });
    });
  };

  private getMovieDetailsHolder(movieId: number, type: string) {
    return (
      this.movieDetailsMap.get(this.movieDetailsKey(movieId, type)) || {
        status: 'UNINITIALIZED',
      }
    );
  }

  private movieDetailsKey(movieId: number, type: string) {
    return `${this.languageStore.currentLanguage}_${movieId}_${type}`;
  }

  private fetchMovieDetailsNotRequired(movieId: number, type: string): boolean {
    const moviesHolder = this.getMovieDetailsHolder(movieId, type);
    return (moviesHolder && !['UNINITIALIZED'].includes(moviesHolder.status)) || ['FETCHING', 'NOT_FOUND'].includes(moviesHolder.status);
  }
}

export default MovieDetailsStore;
